import { styled } from '@mui/material'
import moment from 'moment'
import React from 'react'

import { TimeoutID } from '@/app/helpers'

import AudioAnalyser from './analyser'
import AudioCounter from './counter'
import { getFileExtension, getSupportedAudioMimeType } from './recorder.utils'
import AudioWrapper from './wrapper'

type Props = {
  isRecording: boolean

  onDisabled: () => void
  onFinish: (file: File) => Promise<void>
}

export type AudioRecorderRef = {
  startRecording: () => Promise<void>
  stopRecording: () => Promise<void>
}

const AudioRecorder = React.forwardRef<AudioRecorderRef, Props>(({ isRecording, onFinish, onDisabled }, ref) => {
  const [current, setCurrent] = React.useState(moment.duration(0, 'seconds'))

  const analyserRef = React.useRef<AnalyserNode | undefined>(undefined)
  const recorderRef = React.useRef<MediaRecorder | undefined>(undefined)

  const startRecording = React.useCallback(
    () =>
      new Promise<void>((resolve) => {
        const context = new AudioContext()
        analyserRef.current = context.createAnalyser()

        navigator.mediaDevices
          .getUserMedia({
            audio: true,
            video: false,
          })
          .then((stream) => {
            if (!analyserRef.current) {
              resolve()
              return
            }

            const chunks: Blob[] = []
            let timeoutRef: TimeoutID
            let startedAt: number

            const src = context.createMediaStreamSource(stream)
            src.connect(analyserRef.current)

            // Pick a supported mime type (browser-dependent)
            const selectedMimeType = getSupportedAudioMimeType()

            if (!selectedMimeType) {
              // If there's no supported mime type, disable recording
              onDisabled()
              resolve()
              return
            }

            const newRecorder = new window.MediaRecorder(
              stream,
              selectedMimeType ? { mimeType: selectedMimeType } : undefined,
            )
            newRecorder.ondataavailable = (event: BlobEvent) => {
              if (event.data && event.data.size > 0) {
                chunks.push(event.data)
              }
            }

            newRecorder.onstart = () => {
              startedAt = Date.now()
              // update duration periodically
              const updateDuration = () => {
                setCurrent(moment.duration(Date.now() - startedAt, 'milliseconds'))
                timeoutRef = setTimeout(() => {
                  updateDuration()
                }, 500)
              }
              updateDuration()
              resolve() // resolve once started
            }

            newRecorder.onstop = () => {
              stream.getTracks().forEach((track) => track.stop())
              src.disconnect()
              const type = selectedMimeType
              const blob = new Blob(chunks, { type })
              const timestamp = moment(startedAt).format('YYYY-MM-DD')
              const ext = getFileExtension(type)

              const file = new File([blob], `recording-${timestamp}.${ext}`, { type })
              onFinish(file)
              if (timeoutRef) {
                clearTimeout(timeoutRef)
              }
              setCurrent(moment.duration(0, 'seconds'))
            }

            newRecorder.onerror = () => {
              if (timeoutRef) {
                clearTimeout(timeoutRef)
              }
              resolve() // or resolve when there's an error
            }

            newRecorder.start()
            recorderRef.current = newRecorder
          })
          .catch(() => {
            onDisabled()
            resolve()
          })
      }),
    [onDisabled, onFinish],
  )

  const stopRecording = React.useCallback(
    () =>
      new Promise<void>((resolve) => {
        recorderRef.current?.stop()
        analyserRef.current?.disconnect()

        analyserRef.current = undefined
        recorderRef.current = undefined

        resolve()
      }),
    [],
  )

  // Cleanup on unmount
  React.useEffect(
    () => () => {
      recorderRef.current?.stop()
      analyserRef.current?.disconnect()
    },
    [],
  )

  React.useImperativeHandle(
    ref,
    () => ({
      startRecording,
      stopRecording,
    }),
    [startRecording, stopRecording],
  )

  return (
    <AudioWrapper>
      <div data-testid="recorder-container">
        <Container>
          <AudioAnalyser isPlaying={!!recorderRef.current && isRecording} analyser={analyserRef.current} />
        </Container>
      </div>
      <AudioCounter current={current.format('mm:ss', { trim: false })} />
    </AudioWrapper>
  )
})

const Container = styled('div')({
  position: 'absolute',
  top: '0',
  left: '0',
  height: '100%',
  width: '100%',
})

export default AudioRecorder

export type { Props as AudioRecorderProps }
